<template>
  <div>
    <!-- Title -->
    <h3 class="text-center serif">
      {{
        showResetPassword
          ? "Reset Password"
          : isLogin
          ? "Log in"
          : "Create account"
      }}
    </h3>

    <!-- Fixed-height error container (for both errors and success messages) -->
    <div class="error-container">
      <!-- Error message -->
      <transition name="fadeUp">
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
          <a
            v-if="showForgotInError"
            href="#"
            class="forgot-in-error-link"
            @click.prevent="
              showResetPassword = true;
              errorMessage = '';
            "
          >
            Forgot password?
          </a>
        </div>
      </transition>
      <!-- Success message for password reset -->
      <transition name="fadeUp">
        <div v-if="resetSuccess" class="success-message">
          <p>✓ Password reset email sent!</p>
          <p class="spam-warning">Check your inbox (and spam/junk folder).</p>
        </div>
      </transition>
    </div>

    <form
      class="auth-section"
      @submit.prevent="isLogin ? handleLogin() : handleRegister()"
      novalidate
    >
      <!-- Login/Register form fields (hidden when reset password is active) -->
      <template v-if="!showResetPassword">
        <div class="input-group">
          <label for="email" class="visually-hidden">Email</label>
          <input
            id="email"
            v-model="email"
            type="email"
            placeholder="Email"
            required
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
            :aria-describedby="emailError ? 'email-error' : null"
            @input="validateEmail"
          />
          <div
            v-if="emailError && email"
            id="email-error"
            class="validation-error"
          >
            {{ emailError }}
          </div>
        </div>

        <!-- Password field with toggle button inside -->
        <div class="input-group">
          <label for="password" class="visually-hidden">Password</label>
          <div class="password-field">
            <input
              id="password"
              v-model="password"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Password"
              required
              minlength="6"
              :aria-describedby="passwordError ? 'password-error' : null"
              @input="validatePassword"
            />
            <button
              type="button"
              class="toggle-btn"
              @click="togglePassword"
              aria-label="Toggle password visibility"
            >
              <svg class="password-icon icon">
                <use :href="passwordVisible ? hideIconPath : viewIconPath" />
              </svg>
            </button>
          </div>
          <div
            v-if="passwordError && password"
            id="password-error"
            class="validation-error"
          >
            {{ passwordError }}
          </div>
        </div>

        <!-- Forgot Password Link (only in login mode) -->
        <div v-if="isLogin" class="forgot-password-container">
          <a
            href="#"
            class="forgot-password-link"
            @click.prevent="showResetPassword = true"
          >
            Forgot password?
          </a>
        </div>
      </template>

      <!-- Password Reset Form (replaces login form when active) -->
      <div v-if="showResetPassword" class="reset-password-section">
        <p class="reset-instructions">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>
        <div class="input-group">
          <label for="resetEmail" class="visually-hidden"
            >Email for password reset</label
          >
          <input
            id="resetEmail"
            v-model="resetEmail"
            type="email"
            placeholder="Email"
            required
            @keyup.enter="handlePasswordReset"
          />
        </div>
        <div class="reset-buttons">
          <button
            type="button"
            class="submit-button"
            @click="handlePasswordReset"
            :disabled="isProcessingReset"
          >
            <span v-if="!isProcessingReset">Send Reset Link</span>
            <span v-else class="processing-state">
              <span class="spinner"></span>
              Sending...
            </span>
          </button>
          <button
            type="button"
            class="back-to-login-btn"
            @click="
              showResetPassword = false;
              resetEmail = '';
              resetSuccess = false;
            "
          >
            Back to Log in
          </button>
        </div>
      </div>

      <!-- Confirm Password field only in registration mode -->
      <template v-if="!showResetPassword">
        <div v-if="!isLogin" class="input-group">
          <label for="confirmPassword" class="visually-hidden"
            >Confirm Password</label
          >
          <div class="password-field">
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Confirm Password"
              required
              minlength="6"
              :aria-describedby="
                confirmPasswordError ? 'confirm-password-error' : null
              "
              @input="validateConfirmPassword"
            />
          </div>
          <div
            v-if="confirmPasswordError && confirmPassword"
            id="confirm-password-error"
            class="validation-error"
          >
            {{ confirmPasswordError }}
          </div>
        </div>

        <!-- Confidentiality Agreement section (only in registration mode) -->
        <div v-if="!isLogin" class="confidentiality-section">
          <div class="agreement-header">
            <h4 class="confidentiality-title">Confidentiality Agreement</h4>
            <small class="agreement-required"><em>Required</em></small>
          </div>
          <div class="confidentiality-content compact" ref="agreementContainer">
            <p>
              <strong>Confidential Information:</strong> All materials,
              including but not limited to concepts, stories, characters,
              scripts, visual content, and business documentation, are
              confidential and proprietary to The Path and its creators.
            </p>

            <p>
              <strong>Non-Disclosure:</strong> You agree not to share,
              reproduce, distribute, or disclose any part of this material - in
              whole or in part - without prior written authorization from The
              Path.
            </p>

            <p>
              <strong>Intellectual Property:</strong> All creative and
              intellectual property rights remain the sole and exclusive
              property of The Path and its affiliated entities. Access does not
              grant any license, ownership, or usage rights.
            </p>

            <p>
              <strong>Limited Access:</strong> Access is granted for private,
              evaluative, or viewing purposes only. No commercial, promotional,
              or derivative use of this content is permitted.
            </p>

            <p>
              <strong>Termination & Legal Protection:</strong> The Path reserves
              the right to suspend or revoke access at any time in the event of
              a breach of these terms. Unauthorized use or disclosure may result
              in legal action.
            </p>
          </div>
          <div class="agreement-checkbox">
            <input
              type="checkbox"
              id="agreementCheckbox"
              v-model="agreementAccepted"
              required
              aria-required="true"
              @click.stop
            />
            <label for="agreementCheckbox" @click.stop>
              I have read and agree to the Confidentiality Agreement
            </label>
          </div>
          <div
            v-if="!agreementAccepted && attemptedSubmit && !isLogin"
            class="validation-error"
          >
            You must accept the Confidentiality Agreement to continue
          </div>
        </div>

        <div
          v-if="!showResetPassword"
          class="largestMarginTop column flex-center"
        >
          <!-- Main Login/Register button -->
          <button
            type="submit"
            class="submit-button"
            :disabled="(!isLogin && !agreementAccepted) || isProcessing"
          >
            <span v-if="!isProcessing">
              {{ isLogin ? "Log in" : "Create account" }}
            </span>
            <span v-else class="processing-state">
              <span class="spinner"></span>
              Processing...
            </span>
          </button>

          <span class="grey-darker">or</span>

          <!-- Google sign-in button with icon -->
          <button
            type="button"
            class="google-btn"
            @click="handleGoogleSignIn"
            :disabled="(!isLogin && !agreementAccepted) || isProcessing"
          >
            <img
              src="@/assets/icons/google.png"
              alt="Google Icon"
              class="google-icon"
            />
            <span>{{
              isLogin ? "Log in with Google" : "Sign up with Google"
            }}</span>
          </button>
        </div>
      </template>
    </form>

    <!-- Login agreement and toggle link (hidden when reset password is active) -->
    <div v-if="!showResetPassword" class="toggle-section">
      <span v-if="isLogin">
        No account?
        <a href="#" @click.prevent="isLogin = false">Sign up</a>
      </span>
      <span v-else>
        Have an account?
        <a href="#" @click.prevent="isLogin = true">Log in</a>
      </span>
      <div v-if="isLogin" class="login-agreement">
        <small>
          By logging in, you agree to the
          <a href="#" @click.prevent="showPreLoginDisclaimer = true">
            Confidentiality Agreement
          </a>
        </small>
      </div>
    </div>

    <PreLoginLegalDisclaimer
      v-if="showPreLoginDisclaimer"
      @close="showPreLoginDisclaimer = false"
    />
  </div>
</template>

<script>
import { ref, watch } from "vue";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { auth } from "@/firebase";
import PreLoginLegalDisclaimer from "@/components/GenericComponents/PreLoginLegalDisclaimer.vue";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

export default {
  name: "AuthOverlay",
  components: {
    PreLoginLegalDisclaimer,
  },
  emits: ["authenticated", "agreement-signed"],
  setup(props, { emit }) {
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const isLogin = ref(true);
    const errorMessage = ref("");
    const passwordVisible = ref(false);
    const agreementAccepted = ref(false);
    const agreementContainer = ref(null);
    const emailError = ref("");
    const passwordError = ref("");
    const confirmPasswordError = ref("");
    const attemptedSubmit = ref(false);
    const isProcessingGoogle = ref(false);
    const showGoogleAgreement = ref(false);
    const tempGoogleUser = ref(null);
    const googleAgreementAccepted = ref(false);
    const showAgreementModal = ref(false);
    const showPreLoginDisclaimer = ref(false);
    const isProcessing = ref(false);

    // Password reset state
    const showResetPassword = ref(false);
    const resetEmail = ref("");
    const isProcessingReset = ref(false);
    const resetSuccess = ref(false);
    const showForgotInError = ref(false);

    // Load SVG paths using require
    const viewIconPath = require("@/assets/iconset.svg") + "#view";
    const hideIconPath = require("@/assets/iconset.svg") + "#hide";

    function togglePassword() {
      passwordVisible.value = !passwordVisible.value;
    }

    function validateEmail() {
      const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      emailError.value = emailPattern.test(email.value)
        ? ""
        : "Please enter a valid email address.";
    }

    function validatePassword() {
      passwordError.value =
        password.value.length >= 6
          ? ""
          : "Password must be at least 6 characters long.";
    }

    function validateConfirmPassword() {
      confirmPasswordError.value =
        confirmPassword.value === password.value
          ? ""
          : "Passwords do not match.";
    }

    async function checkConfidentialityAgreement() {
      try {
        const user = auth.currentUser;
        if (!user) return true; // Treat as needs agreement if no user

        const db = getFirestore();
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        // New users need agreement (no document)
        if (!userDoc.exists()) return true;

        const userData = userDoc.data();
        // Existing users need agreement if field is missing
        return !userData.confidentiality_agreement_signed_at;
      } catch (error) {
        return true; // Default to requiring agreement
      }
    }

    async function handleLogin() {
      errorMessage.value = "";
      attemptedSubmit.value = true;
      if (!email.value || !password.value) {
        errorMessage.value = "Please enter both email and password.";
        return;
      }
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email.value,
          password.value
        );
        const user = userCredential.user;
        const db = getFirestore();
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        // Update agreement status if needed and always update last login
        const updates = {
          last_logged_in: new Date(),
        };

        let needsAgreement = false;
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (!userData.confidentiality_agreement_signed) {
            updates.confidentiality_agreement_signed = true;
            updates.confidentiality_agreement_signed_at = new Date();
            needsAgreement = true;
          }
          await updateDoc(userRef, updates);
        } else {
          // New user - create document with agreement accepted
          await setDoc(userRef, {
            email: user.email,
            privileges: "visitor",
            createdAt: new Date(),
            confidentiality_agreement_signed: true,
            confidentiality_agreement_signed_at: new Date(),
            last_logged_in: new Date(),
          });
          needsAgreement = true;
        }

        // Only emit authenticated if no agreement was needed
        if (needsAgreement) {
          emit("agreement-signed");
        } else {
          emit("authenticated");
        }
      } catch (error) {
        showForgotInError.value = false; // Reset before setting new error
        if (error.code === "auth/wrong-password") {
          errorMessage.value = "The email and password do not match.";
          showForgotInError.value = true;
        } else if (error.code === "auth/invalid-credential") {
          errorMessage.value = "Incorrect email or password.";
          showForgotInError.value = true;
        } else if (error.code === "auth/user-not-found") {
          errorMessage.value = "No account found with this email.";
        } else if (error.code === "auth/invalid-email") {
          errorMessage.value =
            "The email address you entered is not valid. Please check for typos and try again.";
        } else if (error.code === "auth/invalid-credential") {
          errorMessage.value =
            "Oops! Something went wrong. Please try again later.";
        } else {
          errorMessage.value =
            error?.message ||
            "Oops! Something went wrong. Please try again later.";
        }
      }
    }

    async function handleRegister() {
      errorMessage.value = "";
      attemptedSubmit.value = true;
      if (!email.value || !password.value || !confirmPassword.value) {
        errorMessage.value = "Please fill in all fields.";
        return;
      }
      if (password.value !== confirmPassword.value) {
        errorMessage.value = "Passwords do not match.";
        return;
      }
      if (!agreementAccepted.value) {
        errorMessage.value =
          "You must accept the Confidentiality Agreement to create an account.";
        return;
      }

      isProcessing.value = true;

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email.value,
          password.value
        );
        const user = userCredential.user;
        const db = getFirestore();
        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          privileges: "visitor", // default privileges
          createdAt: new Date(),
          confidentiality_agreement_signed: true,
          confidentiality_agreement_signed_at: new Date(),
          last_logged_in: new Date(),
        });

        // Show success toast
        const { toast } = await import("vue3-toastify");
        toast.success("Account created successfully!", {
          autoClose: 3000,
          position: "top-center",
        });

        emit("authenticated");
      } catch (error) {
        if (error.code === "auth/email-already-in-use") {
          errorMessage.value =
            "This email is already registered. Please log in or use a different email.";
        } else if (error.code === "auth/weak-password") {
          errorMessage.value =
            "Your password is too weak. Please choose a password with at least 6 characters.";
        } else {
          errorMessage.value =
            error?.message || "Registration failed. Please try again.";
        }
      } finally {
        isProcessing.value = false;
      }
    }

    async function handleGoogleSignIn() {
      errorMessage.value = "";
      isProcessingGoogle.value = true;

      try {
        const provider = new GoogleAuthProvider();
        const userCredential = await signInWithPopup(auth, provider);
        const user = userCredential.user;
        const db = getFirestore();
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          // Existing user - update last login
          const updates = {
            last_logged_in: new Date(),
          };
          await updateDoc(doc(db, "users", user.uid), updates);
          emit("authenticated");
        } else {
          // New Google user - create doc WITHOUT auto-accepting NDA
          // The NDA modal will be shown by HomeView based on confidentiality_agreement_signed: false
          await setDoc(doc(db, "users", user.uid), {
            email: user.email,
            privileges: "visitor",
            createdAt: new Date(),
            confidentiality_agreement_signed: false,
            last_logged_in: new Date(),
          });
          emit("authenticated");
        }
      } catch (error) {
        errorMessage.value =
          error?.message || "Google Sign-In failed. Please try again.";
      } finally {
        isProcessingGoogle.value = false;
      }
    }

    async function handlePasswordReset() {
      if (!resetEmail.value) {
        errorMessage.value = "Please enter your email address.";
        return;
      }

      isProcessingReset.value = true;
      errorMessage.value = "";
      resetSuccess.value = false;

      try {
        await sendPasswordResetEmail(auth, resetEmail.value);
        resetSuccess.value = true;
        // Auto-hide success after 5 seconds
        setTimeout(() => {
          resetSuccess.value = false;
        }, 5000);
      } catch (error) {
        if (error.code === "auth/user-not-found") {
          // For security, show same success message even if email doesn't exist
          resetSuccess.value = true;
          setTimeout(() => {
            resetSuccess.value = false;
          }, 5000);
        } else if (error.code === "auth/invalid-email") {
          errorMessage.value = "Please enter a valid email address.";
        } else if (error.code === "auth/too-many-requests") {
          errorMessage.value = "Too many attempts. Please try again later.";
        } else {
          errorMessage.value = "Failed to send reset email. Please try again.";
        }
      } finally {
        isProcessingReset.value = false;
      }
    }

    async function handleGoogleRegistration() {
      if (!googleAgreementAccepted.value) {
        errorMessage.value =
          "You must accept the Confidentiality Agreement to complete registration.";
        return;
      }

      // Watch for changes in isLogin to reset agreement state
      watch(isLogin, (newValue) => {
        resetAgreementState();
      });
    }

    return {
      email,
      password,
      confirmPassword,
      isLogin,
      errorMessage,
      passwordVisible,
      agreementAccepted,
      agreementContainer,
      emailError,
      passwordError,
      confirmPasswordError,
      attemptedSubmit,
      togglePassword,
      validateEmail,
      validatePassword,
      validateConfirmPassword,
      handleLogin,
      handleRegister,
      handleGoogleSignIn,
      viewIconPath,
      hideIconPath,
      showAgreementModal,
      showPreLoginDisclaimer,
      isProcessing,
      showResetPassword,
      resetEmail,
      isProcessingReset,
      resetSuccess,
      showForgotInError,
      handlePasswordReset,
    };
  },
};
</script>

<style scoped>
/* Reserve fixed space for error messages */
.error-container {
  min-height: 30px;
  margin-top: var(--clickableHeight);
}

/* We'll use a transition named "fadeUp" instead of a direct animation on the class. */
.error-message {
  color: var(--red);
  background-color: var(--bg-main-dark);
  text-align: center;
}

/* The fadeUp transition */
.fadeUp-enter-active,
.fadeUp-leave-active {
  transition: 0.4s ease-out;
}
.fadeUp-enter-from,
.fadeUp-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Container for auth fields */
.auth-section {
  margin: var(--clickableHeight) 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  align-items: center;
}

/* Password field container with relative positioning */
.password-field {
  position: relative;
  display: flex;
  align-items: center;
}
.password-field input {
  width: 100%;
  padding-right: 2.5rem; /* space for toggle icon */
}
.toggle-btn {
  position: absolute;
  right: var(--largeSpace);
  top: calc(50% - calc(var(--space) / 2));
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

/* Confidentiality Agreement styling */
.confidentiality-section {
  margin-top: var(--space);
  border-radius: var(--border-radius-small);
  padding: var(--smallSpace);
  /* border: 1px solid var(--border-mid);
  background-color: rgba(255, 255, 255, 0.05); */
}

.agreement-checkbox input[type="checkbox"] {
  height: calc(var(--clickableHeight) / 2.5);
  width: calc(var(--clickableHeight) / 2.5);
}

.login-agreement {
  margin: var(--space);
}

.agreement-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--smallSpace);
}

.confidentiality-title {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-white);
}

.agreement-required {
  color: var(--red);
  font-size: 0.8rem;
}

.confidentiality-content {
  max-height: 160px;
  overflow-y: auto;
  padding-right: var(--smallSpace);
  margin-bottom: var(--smallSpace);
  font-size: 0.8rem;
  color: var(--text-white);
  opacity: 0.9;
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-small);
  padding: var(--space);
  background-color: rgba(0, 0, 0, 0.2);
  opacity: 0.6;
}

.confidentiality-content.compact p {
  margin-bottom: var(--smallSpace);
  font-size: 0.8rem;
  line-height: 1.3;
}

.agreement-checkbox {
  display: flex;
  align-items: flex-start;
  gap: var(--smallSpace);
  margin-top: var(--smallSpace);
}

.agreement-checkbox label {
  font-size: 0.8rem;
  line-height: 1.3;
}

.agreement-checkbox label.disabled {
  opacity: 0.7;
}

.scroll-hint {
  font-size: 0.7rem;
  color: var(--grey);
  font-style: italic;
  display: inline-block;
  margin-left: 4px;
}

/* Google button styling */
.google-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.google-icon {
  width: 20px;
  height: 20px;
}

/* Toggle link styling */
.toggle-section {
  margin-top: 1rem;
  text-align: center;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.validation-error {
  color: var(--red);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* Processing state */
.processing-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: var(--text-white);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Forgot password link */
.forgot-password-container {
  width: 100%;
  text-align: center;
  margin-top: calc(var(--smallSpace) * -1);
  margin-bottom: var(--smallSpace);
}

.forgot-password-link {
  color: var(--grey);
  font-size: 0.85rem;
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-password-link:hover {
  color: var(--text-white);
  text-decoration: underline;
}

/* Forgot password link inside error message */
.forgot-in-error-link {
  display: block;
  margin-top: var(--smallSpace);
  color: var(--purple-light);
  font-size: 0.9rem;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.forgot-in-error-link:hover {
  color: var(--text-white);
}

/* Password reset section */
.reset-password-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space);
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.reset-instructions {
  text-align: center;
  color: var(--text-white);
  opacity: 0.9;
  font-size: 0.9rem;
  line-height: 1.4;
}

.reset-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--smallSpace);
  width: 100%;
  align-items: center;
}

.back-to-login-btn {
  background: transparent;
  border: none;
  color: var(--grey);
  font-size: 0.85rem;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: var(--smallSpace);
}

.back-to-login-btn:hover {
  color: var(--text-white);
  text-decoration: underline;
}

/* Success message in error container */
.success-message {
  color: var(--green, #4ade80);
  text-align: center;
  font-size: 0.9rem;
}

.success-message p {
  margin: 0;
}

.success-message .spam-warning {
  font-size: 0.8rem;
  opacity: 0.85;
  margin-top: var(--smallSpace);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media only screen and (min-width: 480px) {
  .auth-section {
    width: 400px;
  }
}
</style>
